import { Profile } from '../components/ScreeningProfiles/types';
import { Document } from '../components/ScreeningProfiles/types';

export interface ProfileValidationResult {
  isComplete: boolean;
  missingFields: string[];
  missingDocuments: string[];
}

type DocumentType = 
  | 'IDENTITY_FRONT'
  | 'IDENTITY_BACK'
  | 'EMPLOYER_DECLARATION'
  | 'PAYSLIP_1'
  | 'PAYSLIP_2'
  | 'PAYSLIP_3'
  | 'BANKCARD_COPY'
  | 'LANDLORD_DECLARATION'
  | 'PERSONAL_RECORD'
  | 'UWV_RECORD'
  | 'COMPANY_REGISTRATION'
  | 'INCOME_STATEMENT'
  | 'BALANCE_STATEMENT'
  | 'AOW'
  | 'PENSION';

type EmploymentType = 'EMPLOYEE' | 'SELF_EMPLOYED' | 'PENSION';

interface DocumentRequirement {
  required: boolean;
  label: string;
}

type DocumentRequirements = {
  [key in EmploymentType]: {
    [key in DocumentType]?: DocumentRequirement;
  };
};

const DOCUMENT_REQUIREMENTS: DocumentRequirements = {
  EMPLOYEE: {
    IDENTITY_FRONT: { required: true, label: 'screening_profile.doc_identity_front' },
    IDENTITY_BACK: { required: true, label: 'screening_profile.doc_identity_back' },
    EMPLOYER_DECLARATION: { required: true, label: 'screening_profile.doc_employer_declaration' },
    PAYSLIP_1: { required: true, label: 'screening_profile.doc_payslip_1' },
    PAYSLIP_2: { required: true, label: 'screening_profile.doc_payslip_2' },
    PAYSLIP_3: { required: true, label: 'screening_profile.doc_payslip_3' },
    BANKCARD_COPY: { required: true, label: 'screening_profile.doc_bankcard_copy' },
    LANDLORD_DECLARATION: { required: false, label: 'screening_profile.doc_landlord_declaration' },
    PERSONAL_RECORD: { required: false, label: 'screening_profile.doc_personal_record' },
    UWV_RECORD: { required: false, label: 'screening_profile.doc_uwv_record' }
  },
  SELF_EMPLOYED: {
    IDENTITY_FRONT: { required: true, label: 'screening_profile.doc_identity_front' },
    IDENTITY_BACK: { required: true, label: 'screening_profile.doc_identity_back' },
    COMPANY_REGISTRATION: { required: true, label: 'screening_profile.doc_company_registration' },
    INCOME_STATEMENT: { required: true, label: 'screening_profile.doc_income_statement' },
    BALANCE_STATEMENT: { required: true, label: 'screening_profile.doc_balance_statement' },
    BANKCARD_COPY: { required: true, label: 'screening_profile.doc_bankcard_copy' },
    LANDLORD_DECLARATION: { required: false, label: 'screening_profile.doc_landlord_declaration' },
    PERSONAL_RECORD: { required: false, label: 'screening_profile.doc_personal_record' }
  },
  PENSION: {
    IDENTITY_FRONT: { required: true, label: 'screening_profile.doc_identity_front' },
    IDENTITY_BACK: { required: true, label: 'screening_profile.doc_identity_back' },
    AOW: { required: true, label: 'screening_profile.doc_aow_statement' },
    PENSION: { required: true, label: 'screening_profile.doc_pension_statement' },
    BANKCARD_COPY: { required: true, label: 'screening_profile.doc_bankcard_copy' },
    LANDLORD_DECLARATION: { required: false, label: 'screening_profile.doc_landlord_declaration' },
    PERSONAL_RECORD: { required: false, label: 'screening_profile.doc_personal_record' }
  }
};

export const checkProfileCompleteness = (profile: Profile, documents: Document[] = []): ProfileValidationResult => {
  const missingFields: string[] = [];
  const missingDocuments: string[] = [];
  
  // Required personal information fields
  if (!profile.firstname?.trim()) missingFields.push('screening_profile.firstname');
  if (!profile.surname?.trim()) missingFields.push('screening_profile.surname');
  if (!profile.email?.trim()) missingFields.push('screening_profile.email');
  if (!profile.phoneNumber?.trim()) missingFields.push('screening_profile.phone');
  if (!profile.birthdate?.trim()) missingFields.push('screening_profile.birthdate');
  if (!profile.birthplace?.trim()) missingFields.push('screening_profile.birthplace');
  
  // Required address fields
  if (!profile.street?.trim()) missingFields.push('screening_profile.street');
  if (!profile.houseNumber?.trim()) missingFields.push('screening_profile.house_number');
  if (!profile.postalCode?.trim()) missingFields.push('screening_profile.postal_code');
  if (!profile.city?.trim()) missingFields.push('screening_profile.city');
  if (!profile.country?.trim()) missingFields.push('screening_profile.country');
  if (!profile.province?.trim()) missingFields.push('screening_profile.province');
  
  // Required income information
  if (!profile.income || profile.income <= 0) missingFields.push('screening_profile.income');
  
  // Required profile type information
  if (!profile.employmentType?.trim()) missingFields.push('screening_profile.employment_type');
  if (!profile.screeningType?.trim()) missingFields.push('screening_profile.screening_type');

  // Check required documents based on employment type
  if (profile.employmentType && DOCUMENT_REQUIREMENTS[profile.employmentType as EmploymentType]) {
    const requiredDocs = DOCUMENT_REQUIREMENTS[profile.employmentType as EmploymentType];
    const uploadedDocTypes = documents.map(doc => doc.screeningDocumentType);

    Object.entries(requiredDocs).forEach(([docType, docInfo]) => {
      if (docInfo?.required && !uploadedDocTypes.includes(docType as DocumentType)) {
        missingDocuments.push(docInfo.label);
      }
    });
  }

  return {
    isComplete: missingFields.length === 0 && missingDocuments.length === 0,
    missingFields,
    missingDocuments
  };
}; 